import 'lenis/dist/lenis.css'
import { ReactLenis, useLenis, type LenisProps } from 'lenis/react'
import { useEffect, type ReactNode } from 'react'
import { IS_DEV, isDebug } from '@/lib/flags'
import { gsap, refreshScroll, ScrollTrigger } from '@/lib/gsap'
import { useAppStore } from '@/lib/store'

/** Props for {@link SmoothScroll}. */
export interface SmoothScrollProps {
  children: ReactNode
}

/**
 * Root smooth scrolling: one Lenis instance on `<html>`, driven by the GSAP ticker so
 * Lenis, ScrollTrigger and every tween share a single rAF.
 *
 * - Scroll is locked (`lenis.stop()`) while `phase === 'boot'`.
 * - Under reduced motion, wheel smoothing is off (native scrolling).
 * - Get the instance anywhere with `useLenis()` from `lenis/react`, e.g.
 *   `lenis?.scrollTo('#pipeline')`. Read velocity with `useLenis((l) => l.velocity)`.
 * - Add `data-lenis-prevent` to inner scroll areas (menus, modals) to opt out.
 */
export function SmoothScroll({ children }: SmoothScrollProps) {
  const reducedMotion = useAppStore((state) => state.reducedMotion)

  const options: LenisProps['options'] = {
    autoRaf: false,
    smoothWheel: !reducedMotion,
    lerp: 0.1,
    anchors: true,
  }

  return (
    <ReactLenis root options={options}>
      <LenisDriver />
      {children}
    </ReactLenis>
  )
}

/** Wires the Lenis instance to GSAP, the app phase and the debug handle. Renders nothing. */
function LenisDriver() {
  const lenis = useLenis()
  const phase = useAppStore((state) => state.phase)

  // One clock: the GSAP ticker drives Lenis, and Lenis scroll events update ScrollTrigger.
  useEffect(() => {
    if (!lenis) return
    const unsubscribe = lenis.on('scroll', ScrollTrigger.update)
    const tick = (time: number) => lenis.raf(time * 1000)
    gsap.ticker.add(tick)
    gsap.ticker.lagSmoothing(0)
    return () => {
      unsubscribe()
      gsap.ticker.remove(tick)
      gsap.ticker.lagSmoothing(500, 33)
    }
  }, [lenis])

  // Phase changes must preserve the lock if the menu opened during the loader's exit.
  // Menu owns its open/close transitions, including unlocking after its closing animation.
  useEffect(() => {
    if (!lenis) return
    if (phase === 'boot' || useAppStore.getState().menuOpen) lenis.stop()
    else lenis.start()
  }, [lenis, phase])

  // Layout settles twice: when web fonts swap in, and when the intro finishes.
  useEffect(() => {
    let cancelled = false
    void document.fonts.ready.then(() => {
      if (!cancelled) refreshScroll()
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (phase === 'ready') refreshScroll()
  }, [phase])

  // Debug handle for the console and scripts/snap.mjs.
  useEffect(() => {
    if (!IS_DEV && !isDebug) return
    window.__vk = { gsap, ScrollTrigger, lenis, store: useAppStore }
    return () => {
      delete window.__vk
    }
  }, [lenis])

  return null
}
